import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Autocomplete, Body, Empty, NoOptionsMessage, Theme } from '@routific/hive';
import { validateTag, isDuplicateTag, isAtTagLimit, MAX_TAGS, MAX_TAG_LENGTH } from './tagValidation';

export interface TagsInputLabels {
  placeholder?: string;
  tagLimit?: string;
  tagTooLong?: string;
  invalidChars?: string;
  addTag?: (tag: string) => string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  noResultsTitle?: string;
  noResultsDescription?: string;
}

export interface TagsInputProps {
  value?: string[];
  onChange?: (tags: string[]) => void;
  options?: string[];
  onInputChange?: (value: string) => void;
  onDropdownOpen?: () => void;
  labels?: TagsInputLabels;
  placeholder?: string;
  id?: string;
  emptyStateImage?: ReactNode;
  loading?: boolean;
}

const DEFAULT_LABELS: Required<TagsInputLabels> = {
  placeholder: 'No tags selected',
  tagLimit: `Maximum of ${MAX_TAGS} tags allowed.`,
  tagTooLong: `Tags must be ${MAX_TAG_LENGTH} characters or less.`,
  invalidChars: 'Tags can only contain letters, numbers, dashes, and spaces.',
  addTag: (tag: string) => `Add "${tag}"`,
  emptyStateTitle: 'Add your first tag',
  emptyStateDescription: 'Type the name of your tag, then hit enter or click the button below to add it as a new tag!',
  noResultsTitle: 'No tags found',
  noResultsDescription: 'Hit enter or click the button below to create a new tag with this name, or try another search term.',
};

const AddTagOption = styled.div<{ $focused?: boolean }>`
  ${Theme.Typography.Body.toCssString()}
  color: ${Theme.Colors.primary5};
  padding: ${Theme.Spacing.mdSpacing} ${Theme.Spacing.mdSpacing};
  cursor: pointer;
  border-top: 1px solid ${Theme.Colors.gray3};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background-color: ${({ $focused }) => ($focused ? Theme.Colors.primary1 : 'transparent')};

  &:hover {
    background-color: ${Theme.Colors.primary1};
  }

  &:active {
    background-color: ${Theme.Colors.primary2};
  }
`;

const ErrorBanner = styled.div`
  padding: ${Theme.Spacing.mdSpacing} ${Theme.Spacing.mdSpacing};
  border-bottom: 1px solid ${Theme.Colors.gray3};
`;

const Wrapper = styled.div`
  .MuiOutlinedInput-notchedOutline {
    top: 0;

    legend {
      display: none;
    }
  }

  .MuiOutlinedInput-root > :not(.MuiAutocomplete-input):not(.MuiOutlinedInput-notchedOutline) {
    max-width: calc(100% - 6px);
    overflow: hidden;
  }
`;

const TagsInput: React.FC<TagsInputProps> = ({
  value = [],
  onChange,
  options = [],
  onInputChange: onInputChangeProp,
  onDropdownOpen,
  labels: labelsProp,
  placeholder,
  id = 'tags-autocomplete',
  emptyStateImage,
  loading = false,
}) => {
  const labels = useMemo(() => ({ ...DEFAULT_LABELS, ...labelsProp }), [labelsProp]);
  const enterPressedRef = useRef(false);
  const arrowNavigatedRef = useRef(false);
  const footerFocusedRef = useRef(false);
  const [footerFocused, setFooterFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const trimmedInput = inputValue.trim();
  const listboxId = `${id}-listbox`;

  const updateFooterFocus = useCallback((focused: boolean) => {
    footerFocusedRef.current = focused;
    setFooterFocused(focused);
  }, []);

  useEffect(() => {
    onInputChangeProp?.(trimmedInput);
    updateFooterFocus(false);
  }, [trimmedInput, onInputChangeProp, updateFooterFocus]);

  const atLimit = isAtTagLimit(value);
  const overLimit = value.length > MAX_TAGS;
  const inputError = trimmedInput.length > 0 ? validateTag(trimmedInput) : null;
  const showLimitError = overLimit || (atLimit && trimmedInput.length > 0);
  const hasError = showLimitError || !!inputError;

  const errorMessage = useMemo(() => {
    const messages: string[] = [];
    if (showLimitError) messages.push(labels.tagLimit);
    if (inputError === 'tagTooLong') messages.push(labels.tagTooLong);
    if (inputError === 'invalidTagCharacters') messages.push(labels.invalidChars);
    return messages.length > 0 ? messages.join(' ') : undefined;
  }, [showLimitError, inputError, labels]);

  const mergedOptions = useMemo(() => {
    const optionSet = new Set(options.map((o) => o.toLowerCase()));
    if (trimmedInput) {
      const search = trimmedInput.toLowerCase();
      const matchingSelected = value.filter(
        (tag) => !optionSet.has(tag.toLowerCase()) && tag.toLowerCase().includes(search),
      );
      return [...matchingSelected, ...options];
    }
    const unsaved = value.filter((tag) => !optionSet.has(tag.toLowerCase()));
    return [...unsaved, ...options];
  }, [options, value, trimmedInput]);

  const addTag = useCallback((tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || validateTag(trimmed)) return;
    if (isDuplicateTag(trimmed, value)) return;
    if (isAtTagLimit(value)) return;
    onChange?.([...value, trimmed]);
    setInputValue('');
  }, [value, onChange]);

  const inputMatchesExisting = isDuplicateTag(trimmedInput, [...options, ...value]);

  const showAddOption = trimmedInput.length > 0
    && !inputError
    && !inputMatchesExisting
    && !atLimit;

  const noOptionsContent = useMemo(() => {
    if (hasError) return <></>;
    if (loading) return undefined;
    if (trimmedInput) {
      return <NoOptionsMessage title={labels.noResultsTitle} subtitle={labels.noResultsDescription} />;
    }
    if (mergedOptions.length === 0) {
      return (
        <Empty
          size="small"
          image={emptyStateImage}
          title={labels.emptyStateTitle}
          description={labels.emptyStateDescription}
        />
      );
    }
    return undefined;
  }, [hasError, loading, trimmedInput, mergedOptions.length, emptyStateImage, labels]);

  const isLastOptionFocused = useCallback(() => {
    const listbox = document.getElementById(listboxId);
    if (!listbox) return true;
    const opts = listbox.querySelectorAll('[role="option"]');
    if (opts.length === 0) return true;
    const lastOpt = opts[opts.length - 1];
    const input = document.getElementById(id);
    const activeId = input?.getAttribute('aria-activedescendant');
    return activeId === lastOpt.id;
  }, [listboxId, id]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      if (footerFocusedRef.current) {
        e.preventDefault();
        return;
      }
      if (showAddOption && isLastOptionFocused()) {
        e.preventDefault();
        updateFooterFocus(true);
        return;
      }
      arrowNavigatedRef.current = true;
      return;
    }

    if (e.key === 'ArrowUp') {
      if (footerFocusedRef.current) {
        e.preventDefault();
        updateFooterFocus(false);
        arrowNavigatedRef.current = true;
        return;
      }
      arrowNavigatedRef.current = true;
      return;
    }

    if (e.key === 'Enter') {
      if (footerFocusedRef.current) {
        e.preventDefault();
        addTag(trimmedInput);
        updateFooterFocus(false);
        return;
      }

      if (!trimmedInput || arrowNavigatedRef.current) {
        arrowNavigatedRef.current = false;
        return;
      }

      enterPressedRef.current = true;
      requestAnimationFrame(() => { enterPressedRef.current = false; });
      e.preventDefault();
      addTag(trimmedInput);
      return;
    }

    arrowNavigatedRef.current = false;
    updateFooterFocus(false);
  }, [trimmedInput, addTag, showAddOption, isLastOptionFocused, updateFooterFocus]);

  const addTagFooter = showAddOption ? (
    <AddTagOption
      data-testid="tags-option-create"
      $focused={footerFocused}
      role="option"
      aria-selected={footerFocused}
      id={`${listboxId}-create-option`}
      onMouseDown={(e) => { e.preventDefault(); }}
      onClick={() => { addTag(trimmedInput); }}
    >
      {`+ ${labels.addTag(trimmedInput)}`}
    </AddTagOption>
  ) : null;

  return (
    <Wrapper>
      {footerFocused && (
        <style>{`#${listboxId} .Mui-focused { background-color: transparent !important; }`}</style>
      )}
      <Autocomplete<string>
        id={id}
        testIdPrefix="tag"
        options={mergedOptions}
        value={value}
        onOpen={onDropdownOpen}
        onChange={(newValue) => {
          if (enterPressedRef.current) return;
          if (atLimit && newValue.length > value.length) return;
          onChange?.(newValue);
        }}
        filterOptions={(opts) => [...opts]}
        inputValue={inputValue}
        onInputChange={setInputValue}
        openOnFocus
        forcePopupIcon
        onInputKeyDown={handleInputKeyDown}
        placeholder={placeholder ?? labels.placeholder}
        error={hasError}
        errorText={errorMessage}
        noOptionsComponent={noOptionsContent}
        loading={loading && !hasError}
        disableCloseOnSelect
        renderHeader={errorMessage ? () => (
          <ErrorBanner data-testid="tags-error-banner">
            <Body type="danger">{errorMessage}</Body>
          </ErrorBanner>
        ) : undefined}
        renderFooter={() => addTagFooter}
      />
    </Wrapper>
  );
};

export default TagsInput;
